/* eslint-disable import/no-extraneous-dependencies */
import {
  useEffect,
  useState,
  useRef,
  SetStateAction,
  forwardRef,
  useImperativeHandle
} from 'react'
import { SelectChangeEvent } from '@mui/material'
import {
  hsvaToHsla,
  hsvaToRgba,
  hsvaToHex,
  hexToHsva,
  rgbaToHsva,
  hslaToHsva
} from '@uiw/color-convert'

import TextField from '@/shared/components/TextField/TextField'
import ArrowSelect from '@/shared/components/Select/ArrowSelect'
import styles from './ColorInputs.module.scss'

type ColorFormat = 'rgb' | 'rgba' | 'hsl' | 'hsla' | 'hex' | 'hsv' | 'cmyk'

type ColorInputsProps = {
  color: tinycolor.ColorFormats.HSVA
  onChange: (value: tinycolor.ColorFormats.HSVA, apply?: boolean) => void
  isInputFocused: boolean
  setIsInputFocused: (value: SetStateAction<boolean>) => void
}

const normalizeHsva = (hsva: tinycolor.ColorFormats.HSVA) => ({
  h: Math.round(hsva.h),
  s: hsva.s,
  v: hsva.v,
  a: hsva.a
})

export type ColorInputsHandle = {
  finalizeInput: () => void
}

const ColorInputs = forwardRef<ColorInputsHandle, ColorInputsProps>(
  ({ color, onChange, isInputFocused, setIsInputFocused }, ref) => {
    const [selectedFormat, setSelectedFormat] = useState<ColorFormat>('rgb')

    const rgb = hsvaToRgba(color)
    const hsl = hsvaToHsla(color)

    const [inputValues, setInputValues] = useState<Record<string, string>>({})
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

    const clamp = (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value))

    useImperativeHandle(ref, () => ({
      finalizeInput: () => {
        if (!isInputFocused) return

        if (selectedFormat === 'hex') {
          const value = inputValues.hex ?? ''
          handleInputBlur('hex', value)
        }

        if (selectedFormat === 'rgb') {
          handleInputBlur('r', inputValues.r ?? '')
          handleInputBlur('g', inputValues.g ?? '')
          handleInputBlur('b', inputValues.b ?? '')
        }

        if (selectedFormat === 'hsl') {
          handleInputBlur('h', inputValues.h ?? '')
          handleInputBlur('s', inputValues.s ?? '')
          handleInputBlur('l', inputValues.l ?? '')
        }

        setIsInputFocused(false)
      }
    }))

    useEffect(() => {
      if (!isInputFocused) {
        if (selectedFormat === 'hsv') {
          setInputValues({
            h: String(Math.round(color.h)),
            s: String(Math.round(color.s)),
            v: String(Math.round(color.v))
          })
        } else if (selectedFormat === 'rgb') {
          setInputValues({
            r: String(Math.round(rgb.r)),
            g: String(Math.round(rgb.g)),
            b: String(Math.round(rgb.b))
          })
        } else if (selectedFormat === 'hsl') {
          setInputValues({
            h: String(Math.round(hsl.h)),
            s: String(Math.round(hsl.s)),
            l: String(Math.round(hsl.l))
          })
        } else if (selectedFormat === 'hex') {
          setInputValues({ hex: hsvaToHex(color).toUpperCase() })
        }
      }
    }, [
      selectedFormat,
      isInputFocused,
      color,
      rgb.r,
      rgb.g,
      rgb.b,
      hsl.h,
      hsl.s,
      hsl.l
    ])

    const handleInputChange = (field: string, value: string) => {
      if (selectedFormat === 'hex') {
        let cleanHex = value
          .replace(/^#/, '')
          .replace(/[^a-fA-F0-9]/g, '')
          .toUpperCase()
          .slice(0, 6)
        setInputValues({ hex: `#${cleanHex}` })

        if (cleanHex.length === 0) {
          cleanHex = '000000'
        } else if (cleanHex.length === 1) {
          cleanHex = cleanHex.repeat(6)
        } else if (cleanHex.length === 2) {
          cleanHex = cleanHex.repeat(3)
        } else if (cleanHex.length >= 3 && cleanHex.length < 6) {
          const padChar = cleanHex[0]
          cleanHex = padChar.repeat(6 - cleanHex.length) + cleanHex
        } else if (cleanHex.length > 6) {
          cleanHex = cleanHex.slice(0, 6)
        }

        const newColor = hexToHsva(cleanHex.padEnd(6, '0'))

        onChange(normalizeHsva(newColor), true)
      } else {
        const sanitized = value.replace(/\D/g, '')
        setInputValues(prev => ({ ...prev, [field]: sanitized }))

        if (selectedFormat.includes('rgb')) {
          const r = field === 'r' ? sanitized : inputValues.r
          const g = field === 'g' ? sanitized : inputValues.g
          const b = field === 'b' ? sanitized : inputValues.b

          if ([r, g, b].every(v => /^\d{1,3}$/.test(v))) {
            const newColor = rgbaToHsva({
              r: clamp(+r, 0, 255),
              g: clamp(+g, 0, 255),
              b: clamp(+b, 0, 255),
              a: color.a
            })
            onChange(normalizeHsva(newColor))
          }
        }

        if (selectedFormat.includes('hsl')) {
          const h = field === 'h' ? sanitized : inputValues.h
          const s = field === 's' ? sanitized : inputValues.s
          const l = field === 'l' ? sanitized : inputValues.l

          if ([h, s, l].every(v => /^\d{1,3}$/.test(v))) {
            const newColor = hslaToHsva({
              h: clamp(+h, 0, 360),
              s: clamp(+s, 0, 100),
              l: clamp(+l, 0, 100),
              a: color.a
            })
            onChange(normalizeHsva(newColor))
          }
        }
      }
    }

    const handleInputBlur = (field: string, value: string) => {
      if (selectedFormat === 'hex') {
        let cleanHex = value
          .replace(/^#/, '')
          .replace(/[^a-fA-F0-9]/g, '')
          .toUpperCase()

        if (cleanHex.length === 0) {
          cleanHex = '000000'
        } else if (cleanHex.length === 1) {
          cleanHex = cleanHex.repeat(6)
        } else if (cleanHex.length === 2) {
          cleanHex = cleanHex.repeat(3)
        } else if (cleanHex.length >= 3 && cleanHex.length < 6) {
          const padChar = cleanHex[0]
          cleanHex = padChar.repeat(6 - cleanHex.length) + cleanHex
        } else if (cleanHex.length > 6) {
          cleanHex = cleanHex.slice(0, 6)
        }

        setInputValues({ hex: `#${cleanHex}` })

        const newColor = hexToHsva(cleanHex)

        onChange(normalizeHsva(newColor), true)
        return
      }

      if (selectedFormat.includes('hsl')) {
        const h = field === 'h' ? value : inputValues.h
        const s = field === 's' ? value : inputValues.s
        const l = field === 'l' ? value : inputValues.l

        const fallbackValues = {
          h: h === '' ? '0' : h,
          s: s === '' ? '0' : s,
          l: l === '' ? '0' : l
        }

        setInputValues(prev => ({
          ...prev,
          ...fallbackValues
        }))

        if (
          [fallbackValues.h, fallbackValues.s, fallbackValues.l].every(v =>
            /^\d{1,3}$/.test(v)
          )
        ) {
          const newColor = hslaToHsva({
            h: clamp(+h, 0, 360),
            s: clamp(+s, 0, 100),
            l: clamp(+l, 0, 100),
            a: color.a
          })
          onChange(normalizeHsva(newColor))
        }

        return
      }

      if (selectedFormat.includes('rgb')) {
        const r = field === 'r' ? value : inputValues.r
        const g = field === 'g' ? value : inputValues.g
        const b = field === 'b' ? value : inputValues.b

        const fallbackValues = {
          r: r === '' ? '0' : r,
          g: g === '' ? '0' : g,
          b: b === '' ? '0' : b
        }

        setInputValues(prev => ({
          ...prev,
          ...fallbackValues
        }))

        if (
          [fallbackValues.r, fallbackValues.g, fallbackValues.b].every(v =>
            /^\d{1,3}$/.test(v)
          )
        ) {
          const newColor = rgbaToHsva({
            r: clamp(+r, 0, 255),
            g: clamp(+g, 0, 255),
            b: clamp(+b, 0, 255),
            a: color.a
          })
          onChange(normalizeHsva(newColor))
        }
      }
    }

    const handleFormatChange = (event: SelectChangeEvent) => {
      setSelectedFormat(event.target.value as ColorFormat)
    }

    const handleArrowKeyNavigation = (
      e: React.KeyboardEvent<HTMLInputElement>,
      field: string
    ) => {
      let direction = 0
      if (e.key === 'ArrowUp') {
        direction = 1
      } else if (e.key === 'ArrowDown') {
        direction = -1
      }
      if (e.key === 'Enter') {
        inputRefs.current[field]?.blur()
        if (selectedFormat === 'hex') {
          handleInputBlur(field, inputValues[field] ?? '')
        }
        return
      }
      if (direction === 0) return

      const clamp = (val: number, min: number, max: number) =>
        Math.min(max, Math.max(min, val))

      if (selectedFormat === 'rgb') {
        const currentVal = +inputValues[field] || 0
        const newVal = clamp(currentVal + direction, 0, 255)
        setInputValues(prev => ({ ...prev, [field]: String(newVal) }))

        const r = field === 'r' ? newVal : +inputValues.r
        const g = field === 'g' ? newVal : +inputValues.g
        const b = field === 'b' ? newVal : +inputValues.b

        const newColor = rgbaToHsva({
          r: clamp(+r, 0, 255),
          g: clamp(+g, 0, 255),
          b: clamp(+b, 0, 255),
          a: color.a
        })
        onChange(normalizeHsva(newColor))
      }

      if (selectedFormat === 'hsl') {
        const currentVal = +inputValues[field] || 0
        let newVal = currentVal + direction
        newVal = field === 'h' ? (newVal + 360) % 360 : clamp(newVal, 0, 100)

        setInputValues(prev => ({ ...prev, [field]: String(newVal) }))

        const h = field === 'h' ? newVal : +inputValues.h
        const s = field === 's' ? newVal : +inputValues.s
        const l = field === 'l' ? newVal : +inputValues.l

        const newColor = hslaToHsva({
          h: clamp(+h, 0, 360),
          s: clamp(+s, 0, 100),
          l: clamp(+l, 0, 100),
          a: color.a
        })
        onChange(normalizeHsva(newColor))
      }
    }

    return (
      <div className={styles.colorInputsWrapper}>
        {selectedFormat === 'hex' && (
          <div className={styles.hexInputWrapper}>
            <TextField
              label="HEX"
              value={(inputValues.hex ?? '').toUpperCase()}
              onChange={e => handleInputChange('hex', e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => {
                handleInputBlur('hex', inputValues.hex ?? '')

                setIsInputFocused(false)
              }}
              inputRef={el => {
                inputRefs.current.hex = el
              }}
              onKeyDown={e => {
                handleArrowKeyNavigation(e, 'hex')
              }}
              focused
            />
          </div>
        )}

        {selectedFormat === 'rgb' && (
          <div className={styles.colorInputsChannelWrapper}>
            {['r', 'g', 'b'].map(channel => (
              <TextField
                key={channel}
                label={channel.toUpperCase()}
                value={inputValues[channel] ?? ''}
                onChange={e => handleInputChange(channel, e.target.value)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => {
                  handleInputBlur(channel, inputValues[channel] ?? '')
                  setIsInputFocused(false)
                }}
                onKeyDown={e => handleArrowKeyNavigation(e, channel)}
                inputRef={el => {
                  inputRefs.current[channel] = el
                }}
                focused
              />
            ))}
          </div>
        )}

        {selectedFormat === 'hsl' && (
          <div className={styles.colorInputsChannelWrapper}>
            {['h', 's', 'l'].map(channel => (
              <TextField
                key={channel}
                label={channel.toUpperCase()}
                value={inputValues[channel] ?? ''}
                onChange={e => handleInputChange(channel, e.target.value)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => {
                  handleInputBlur(channel, inputValues[channel] ?? '')
                  setIsInputFocused(false)
                }}
                onKeyDown={e => handleArrowKeyNavigation(e, channel)}
                inputRef={el => {
                  inputRefs.current[channel] = el
                }}
                focused
              />
            ))}
          </div>
        )}

        <ArrowSelect
          options={[
            { label: 'hex', value: 'hex' },
            { label: 'rgb', value: 'rgb' },
            { label: 'hsl', value: 'hsl' }
          ]}
          value={selectedFormat}
          onChange={value =>
            handleFormatChange({ target: { value } } as SelectChangeEvent)
          }
        />
      </div>
    )
  }
)

export default ColorInputs
